import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Wrench, Plus, Trash, CheckCircle, Warning, Clock, ArrowClockwise, CaretDown, CaretUp, X
} from '@phosphor-icons/react';

const CATEGORIES = ['AC Service', 'Water Tank', 'Pest Control', 'Painting', 'Plumbing', 'Electrical', 'Appliance', 'Garden', 'Other'];

const STATUS_CONFIG = {
  overdue: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', icon: Warning, label: 'Overdue' },
  due_soon: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: Clock, label: 'Due Soon' },
  up_to_date: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: CheckCircle, label: 'Up to Date' },
};

export default function MaintenancePage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showMarkDone, setShowMarkDone] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'AC Service', frequency_days: 90, last_done: '', provider_id: '', cost_estimate: 0, notes: '' });
  const [doneForm, setDoneForm] = useState({ date: new Date().toISOString().slice(0, 10), cost: 0, notes: '' });

  const fetchTasks = async () => {
    try {
      const res = await api.get('/maintenance');
      setTasks(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchTasks(); }, [api]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/maintenance', { ...form, frequency_days: Number(form.frequency_days), cost_estimate: Number(form.cost_estimate) });
      setShowForm(false);
      setForm({ title: '', category: 'AC Service', frequency_days: 90, last_done: '', provider_id: '', cost_estimate: 0, notes: '' });
      fetchTasks();
    } catch (err) { console.error(err); }
  };

  const handleMarkDone = async (id) => {
    try {
      await api.post(`/maintenance/${id}/mark-done`, { ...doneForm, cost: Number(doneForm.cost) });
      setShowMarkDone(null);
      setDoneForm({ date: new Date().toISOString().slice(0, 10), cost: 0, notes: '' });
      fetchTasks();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/maintenance/${id}`); fetchTasks(); } catch (e) { console.error(e); }
  };

  const freqLabel = (days) => {
    if (days <= 7) return 'Weekly';
    if (days <= 14) return 'Bi-weekly';
    if (days <= 30) return 'Monthly';
    if (days <= 90) return 'Quarterly';
    if (days <= 180) return 'Half-yearly';
    if (days <= 365) return 'Yearly';
    return `Every ${days} days`;
  };

  // Count by status
  const statusCounts = tasks.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="maintenance-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Home Care</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('maintenance_tracker')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Track recurring home maintenance schedules</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-maintenance-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('add_task')}
        </button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-4 relative z-10">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} className="glass-card p-5 animate-fade-up" data-testid={`maintenance-stat-${key}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${cfg.bg}`}>
                <cfg.icon size={20} weight="duotone" className={cfg.text} />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{statusCounts[key] || 0}</p>
                <p className="text-xs text-neutral-500">{cfg.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tasks List */}
      <div className="space-y-3 relative z-10">
        {tasks.length === 0 ? (
          <div className="text-center py-16">
            <Wrench size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No maintenance tasks added yet</p>
          </div>
        ) : tasks.map(task => {
          const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.up_to_date;
          const isExpanded = expandedHistory === task.maintenance_id;
          return (
            <div key={task.maintenance_id} className={`glass-card p-5 border-l-2 ${status.border} animate-fade-up`} data-testid={`maintenance-card-${task.maintenance_id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                    <status.icon size={20} weight="duotone" className={status.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-white">{task.title}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-neutral-500">
                      {task.category} &middot; {freqLabel(task.frequency_days)} &middot; Est. Rs.{task.cost_estimate?.toLocaleString('en-IN')}
                    </p>
                    <div className="flex gap-4 mt-2 text-[11px] text-neutral-400">
                      <span>Last: {task.last_done || 'Never'}</span>
                      <span>Next: <span className={status.text}>{task.next_due}</span></span>
                    </div>
                    {task.notes && <p className="text-xs text-neutral-500 mt-1">{task.notes}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => { setDoneForm({ date: new Date().toISOString().slice(0, 10), cost: task.cost_estimate || 0, notes: '' }); setShowMarkDone(task.maintenance_id); }}
                    data-testid={`mark-done-${task.maintenance_id}`}
                    className="px-4 py-2 rounded-full border border-emerald-500/20 text-xs font-medium text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1 transition-colors">
                    <CheckCircle size={14} /> {t('mark_done')}
                  </button>
                  {task.history && task.history.length > 0 && (
                    <button onClick={() => setExpandedHistory(isExpanded ? null : task.maintenance_id)}
                      className="px-3 py-2 rounded-full border border-white/10 text-xs text-neutral-400 hover:bg-white/5 flex items-center gap-1 transition-colors">
                      <ArrowClockwise size={14} /> {task.history.length}
                      {isExpanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
                    </button>
                  )}
                  <button onClick={() => handleDelete(task.maintenance_id)} data-testid={`delete-maintenance-${task.maintenance_id}`}
                    className="p-2 rounded-full border border-white/10 text-neutral-500 hover:text-red-400 hover:border-red-400/20 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>

              {/* History */}
              {isExpanded && task.history && task.history.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                  <p className="overline mb-2">History</p>
                  {task.history.slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-center gap-4 text-xs text-neutral-400 bg-white/[0.02] rounded-xl px-4 py-2.5">
                      <span className="text-neutral-300 font-mono">{h.date}</span>
                      <span>Rs.{h.cost?.toLocaleString('en-IN')}</span>
                      {h.notes && <span className="text-neutral-500">{h.notes}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mark Done Modal */}
      {showMarkDone && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowMarkDone(null)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('mark_done')}</h3>
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('date')}</label>
                <input type="date" value={doneForm.date} onChange={e => setDoneForm({ ...doneForm, date: e.target.value })}
                  data-testid="done-date-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('cost')} (Rs.)</label>
                <input type="number" value={doneForm.cost} onChange={e => setDoneForm({ ...doneForm, cost: e.target.value })}
                  data-testid="done-cost-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <input type="text" value={doneForm.notes} onChange={e => setDoneForm({ ...doneForm, notes: e.target.value })}
                  data-testid="done-notes-input" placeholder="Optional notes"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowMarkDone(null)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button onClick={() => handleMarkDone(showMarkDone)} data-testid="confirm-mark-done-btn"
                  className="flex-1 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('add_task')}</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="overline block mb-2">Title</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                  data-testid="maintenance-title-input" placeholder="e.g., AC Service - Living Room"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('category')}</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    data-testid="maintenance-category-select"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('frequency')} (days)</label>
                  <input type="number" value={form.frequency_days} onChange={e => setForm({ ...form, frequency_days: e.target.value })} required
                    data-testid="maintenance-freq-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('last_done')}</label>
                  <input type="date" value={form.last_done} onChange={e => setForm({ ...form, last_done: e.target.value })}
                    data-testid="maintenance-lastdone-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('cost_estimate')} (Rs.)</label>
                  <input type="number" value={form.cost_estimate} onChange={e => setForm({ ...form, cost_estimate: e.target.value })}
                    data-testid="maintenance-cost-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  data-testid="maintenance-notes-input" placeholder="Optional notes"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-maintenance-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
